<template>
  <div :class="containerClass">
    <page-header
      :title="$route.params.id ? 'Edit Quiz' : 'Add Quiz'"
      :breadcrumb="breadcrumb"
      :container-class="null"
      class="mb-32pt"
    />

    <page-separator title="Basic Information" />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <!-- Basic Information -->
      <b-form-group label="Title" label-for="title" label-class="form-label">
        <b-form-input id="title" v-model="quiz.title" required />
      </b-form-group>

      <b-row>
        <b-col md="6">
          <b-form-group label="Max Attempts" label-for="max_attempts" description="0 for unlimited attempts">
            <b-form-input id="max_attempts" type="number" min="0" v-model.number="quiz.max_attempts" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group
            label="Duration (minutes)"
            label-for="duration_minutes"
            description="Leave empty for no time limit"
          >
            <b-form-input id="duration_minutes" type="number" min="1" v-model.number="quiz.duration_minutes" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col md="6">
          <b-form-group label="Total Marks" label-for="total_marks">
            <b-form-input id="total_marks" type="number" min="0" v-model.number="quiz.total_marks" required />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Passing Percentage" label-for="passing_percentage">
            <b-input-group>
              <b-form-input
                id="passing_percentage"
                type="number"
                min="0"
                max="100"
                v-model.number="quiz.passing_percentage"
                required
              />
              <b-input-group-append>
                <span class="input-group-text">%</span>
              </b-input-group-append>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Quiz Settings -->
      <page-separator title="Quiz Settings" />

      <div class="">
        <div class="settings-container">
          <!-- Display Options Section -->
          <div class="settings-section">
            <span class="section-title">
              Display Options
            </span>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">visibility</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Show correct answers</h3>
                  <p class="setting-description">Students can see correct answers after submission</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.show_answers }"
                @click="quiz.show_answers = !quiz.show_answers"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.show_answers">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">history</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Submission history</h3>
                  <p class="setting-description">Allow students to view their past submissions</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.show_submission_history }"
                @click="quiz.show_submission_history = !quiz.show_submission_history"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.show_submission_history">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Quiz Behavior Section -->
          <div class="settings-section">
            <span class="section-title">
              Quiz Behavior
            </span>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">shuffle</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Shuffle questions</h3>
                  <p class="setting-description">Randomize question order for each attempt</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.shuffle_questions }"
                @click="quiz.shuffle_questions = !quiz.shuffle_questions"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.shuffle_questions">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>

            <div class="setting-item">
              <div class="setting-content">
                <div class="setting-icon">
                  <md-icon :style="{ color: getLoggedInUser.linked_entity.primary_color }">do_not_disturb_on</md-icon>
                </div>
                <div class="setting-info">
                  <h3 class="setting-title">Negative marking</h3>
                  <p class="setting-description">Deduct points for incorrect answers</p>
                </div>
              </div>
              <div
                class="toggle-switch"
                :class="{ active: quiz.enable_negative_marking }"
                @click="quiz.enable_negative_marking = !quiz.enable_negative_marking"
              >
                <div class="toggle-slider"></div>
                <div class="toggle-checkmark" v-if="quiz.enable_negative_marking">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <page-separator title="Quiz Questions" />

      <!-- Question Type Buttons -->
      <div class="quiz-questions-section">
        <div class="dropdown-container">
          <button
            class="add-question-btn"
            @click.stop="toggleDropdown"
            :class="{ active: isDropdownOpen }"
            type="button"
          >
            <svg class="plus-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            Add Question
            <svg class="chevron-icon" :class="{ rotated: isDropdownOpen }" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
            </svg>
          </button>

          <div class="dropdown-menu" :class="{ open: isDropdownOpen }">
            <div class="dropdown-item" @click="addQuestion('mcq')">
              <div class="question-type-badge mc">MC</div>
              <span class="question-type-text">Multiple Choice</span>
            </div>
            <div class="dropdown-item" @click="addQuestion('text')">
              <div class="question-type-badge tx">TX</div>
              <span class="question-type-text">Text Answer</span>
            </div>
            <div class="dropdown-item" @click="addQuestion('tf')">
              <div class="question-type-badge tf">TF</div>
              <span class="question-type-text">True/False</span>
            </div>
          </div>
        </div>
      </div>
      <b-form-invalid-feedback :state="!$v.questions.required && $v.questions.$dirty ? false : null"
        >Please add at least one question.</b-form-invalid-feedback
      >
      <!-- Questions List -->
      <draggable
        v-model="questions"
        class="questions-list draggable-cursor"
        @end="updateQuestionOrder"
        handle=".drag-handle"
      >
        <!--  -->
        <div
          v-for="(question, index) in questions"
          :key="question.id"
          class="d-flex align-items-center justify-content-center"
        >
          <!-- Drag Handle -->
          <div class="drag-handle" style="cursor: move;">
            <i class="fas fa-bars icon-16pt mr-2"></i>
          </div>
          <div class="question-card" style="width: 100%">
            <!-- Question Header -->
            <div class="question-header">
              <div class="question-number-badge">
                <span class="question-number">{{ index + 1 }}</span>
                <span class="question-type-label" :class="getTypeClass(question.type)">
                  {{ getTypeLabel(question.type) }}
                </span>
              </div>
              <button @click="deleteQuestion(index)" class="delete-btn" aria-label="Delete question">
                <md-icon class="text-danger">delete</md-icon>
              </button>
            </div>

            <!-- Question Input -->
            <div class="form-group">
              <label class="form-label">Question</label>
              <input
                v-model="question.question_text"
                type="text"
                placeholder="Enter your question"
                class="form-input"
                required
              />
            </div>
            <page-separator>
              <b-link v-b-toggle="`${question.id}-${index}`" class="accordion__toggle p-0">
                <span class="flex">Question Details</span>
                <md-icon class="accordion__toggle-icon">
                  {{ questionVisible[index] ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
                </md-icon>
              </b-link>
            </page-separator>
            <b-collapse
              :id="`${question.id}-${index}`"
              v-model="questionVisible[index]"
              class="accordion__menu"
              accordion="registerOverTimeAccordionId"
              :visible="true"
              @show="questionVisible[index] = true"
              @hide="questionVisible[index] = false"
            >
              <!-- Points Input -->
              <div class="form-group">
                <label class="form-label">Marks</label>
                <input v-model.number="question.marks" type="number" min="1" class="form-input points-input" required />
              </div>

              <!-- Explanation -->
              <div class="form-group">
                <label class="form-label">Explanation</label>
                <textarea v-model="question.explanation" class="form-input"></textarea>
              </div>

              <!-- Multiple Choice Options -->
              <div v-if="question.type === 'mcq'" class="options-section">
                <label class="form-label"
                  >Options
                  <i
                    class="material-icons icon-16pt clickable-item text-muted"
                    v-b-popover.hover.top="'Mark the correct answer.'"
                  >
                    info
                  </i></label
                >
                <div class="options-list">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="option-item">
                    <input
                      :id="`q${question.id}-option${optionIndex}`"
                      :checked="option.is_correct"
                      @change="option.is_correct ? (option.is_correct = false) : (option.is_correct = true)"
                      type="radio"
                      :name="`question-${question.id}`"
                      class="option-radio mr-2"
                    />
                    <input
                      v-model="option.option_text"
                      type="text"
                      required
                      :placeholder="`Option ${optionIndex + 1}`"
                      class="option-input"
                    />
                    <div class="option-actions">
                      <button
                        @click.stop="openExplanationModal(index, optionIndex, option.explanation)"
                        class="explanation-btn"
                        type="button"
                        :title="option.explanation ? 'Edit explanation' : 'Add explanation'"
                      >
                        <md-icon :class="{ 'text-primary': option.explanation }">info</md-icon>
                      </button>
                      <button
                        @click.stop="removeOption(index, optionIndex)"
                        class="remove-option-btn btn-danger"
                        :disabled="question.options.length <= 2"
                        aria-label="Remove option"
                      >
                        <md-icon class="text-danger">delete</md-icon>
                      </button>
                    </div>
                  </div>
                </div>
                <button @click="addOption(index)" class="add-option-btn">
                  + Add Option
                </button>
              </div>

              <!-- Text Answer -->
              <div v-if="question.type === 'text'" class="text-section">
                <div class="form-group">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex">
                    <label class="form-label">Correct Answer</label>
                    <input
                      v-model="option.option_text"
                      type="text"
                      placeholder="Enter the correct answer"
                      class="form-input"
                      required
                    />
                  </div>
                </div>
              </div>

              <!-- True/False -->

              <div v-if="question.type === 'tf'" class="tf-section">
                <label class="form-label">Correct Answer</label>
                <div class="radio-group">
                  <div v-for="(option, optionIndex) in question.options" :key="optionIndex" class="option-item">
                    <label class="radio-label">
                      <input
                        :checked="option.is_correct"
                        @change="option.is_correct ? (option.is_correct = false) : (option.is_correct = true)"
                        type="radio"
                        :name="`tf-${question.id}`"
                        class="radio-input"
                      />
                      <span class="radio-text">{{ option.option_text }}</span>
                    </label>
                  </div>
                </div>
              </div>
            </b-collapse>
          </div>
        </div>
      </draggable>

      <!-- Empty State -->
      <div v-if="questions.length === 0" class="empty-state">
        <p>No questions added yet. Click one of the buttons above to get started!</p>
      </div>

      <!-- Submit Button -->
      <b-btn variant="primary" :disabled="isLoading" type="submit" class="mt-4">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>{{ $route.params.id ? 'Update' : 'Create' }} Quiz</span>
      </b-btn>
    </b-form>

    <!-- Explanation Modal -->
    <b-modal
      v-model="showExplanationModal"
      title="Option Explanation"
      @ok="saveExplanation"
      @hidden="closeExplanationModal"
      ok-title="Save"
      cancel-title="Cancel"
    >
      <b-form-group label="Explanation" label-for="explanation-text">
        <b-form-textarea
          id="explanation-text"
          v-model="currentExplanation.text"
          placeholder="Enter explanation for this option"
          rows="4"
        ></b-form-textarea>
        <small class="text-muted">This explanation will be shown to students when reviewing their answers.</small>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import draggable from 'vuedraggable';
import PageHeader from '@/components/Ui/PageHeader.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import Page from '../../../components/Page.vue';

export default {
  components: {
    PageHeader,
    PageSeparator,
    draggable,
  },
  extends: Page,

  data() {
    return {
      title: this.$route.params.id ? 'Edit Quiz' : 'Add Quiz',
      quiz: {
        title: '',
        max_attempts: 0,
        duration_minutes: null,
        total_marks: 0,
        passing_percentage: 50,
        show_answers: true,
        show_submission_history: true,
        shuffle_questions: false,
        enable_negative_marking: false,
      },
      questionVisible: [],
      isLoading: false,
      questions: [],
      questionIdCounter: 0,
      // For explanation modal
      showExplanationModal: false,
      currentExplanation: {
        questionIndex: null,
        optionIndex: null,
        text: '',
      },
      dragging: false,
      isDropdownOpen: false,
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),

    breadcrumb() {
      return [
        { text: 'Home', to: this.routes.home },
        { text: 'Quizzes', to: { name: 'list-quizzes' } },
        {
          text: this.$route.params.id ? 'Edit' : 'Add',
          active: true,
        },
      ];
    },
  },

  validations: {
    quiz: {
      title: { required },
      max_attempts: { required, minValue: minValue(0) },
      duration_minutes: { minValue: minValue(1) },
      total_marks: { required, minValue: minValue(0) },
      passing_percentage: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(100),
      },
    },
    questions: { required },
  },

  methods: {
    ...mapActions('quizzes', ['createQuiz', 'updateQuiz', 'getQuiz']),

    updateQuestionOrder() {
      // Update the order field of each question based on their new position
      this.questions = this.questions.map((question, index) => ({
        ...question,
        order: index + 1,
      }));
    },
    addQuestion(type) {
      this.isDropdownOpen = false;
      const newQuestion = {
        id: this.questionIdCounter++,
        type: type,
        question_text: '',
        marks: 1,
        explanation: '',
        order: this.questions.length + 1,
        options:
          type === 'mcq'
            ? [
                { option_text: '', is_correct: true, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
                { option_text: '', is_correct: false, explanation: '' },
              ]
            : type === 'tf'
            ? [
                { option_text: 'True', is_correct: true, explanation: '' },
                { option_text: 'False', is_correct: false, explanation: '' },
              ]
            : [{ option_text: '', is_correct: true, explanation: '' }],
      };
      this.questions.push(newQuestion);
      this.questionVisible.push(true);
      // Scroll to the new question
      this.$nextTick(() => {
        const questionCards = document.querySelectorAll('.question-card');
        const lastCard = questionCards[questionCards.length - 1];
        if (lastCard) {
          lastCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });
    },
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    deleteQuestion(index) {
      this.questions.splice(index, 1);
    },

    openExplanationModal(questionIndex, optionIndex, currentExplanation = '') {
      this.currentExplanation = {
        questionIndex,
        optionIndex,
        text: currentExplanation || '',
      };
      this.showExplanationModal = true;
    },

    closeExplanationModal() {
      this.showExplanationModal = false;
      this.currentExplanation = {
        questionIndex: null,
        optionIndex: null,
        text: '',
      };
    },

    saveExplanation() {
      const { questionIndex, optionIndex, text } = this.currentExplanation;
      if (questionIndex !== null && optionIndex !== null) {
        this.$set(this.questions[questionIndex].options[optionIndex], 'explanation', text.trim());
      }
      this.closeExplanationModal();
    },

    addOption(questionIndex) {
      this.questions[questionIndex].options.push({ option_text: '', is_correct: false, explanation: '' });
    },

    removeOption(questionIndex, optionIndex) {
      const question = this.questions[questionIndex];
      if (question.options.length > 2) {
        question.options.splice(optionIndex, 1);
        // Adjust correct answer if needed
        if (question.options.filter(opt => opt.is_correct).length === 0) {
          question.options[0].is_correct = true;
        }
      }
    },
    getTypeLabel(type) {
      const labels = {
        mcq: 'Multiple Choice',
        text: 'Text Answer',
        tf: 'True/False',
      };
      return labels[type] || type;
    },

    getTypeClass(type) {
      return `type-${type}`;
    },
    async onSubmit() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.isLoading = true;
        try {
          const quizData = { ...this.quiz };

          if (this.$route.params.id) {
            await this.updateQuiz({
              id: this.$route.params.id,
              data: { ...quizData, questions: this.questions },
            });
            this.makeToast({ variant: 'success', msg: 'Quiz updated successfully' });
          } else {
            await this.createQuiz({ ...quizData, questions: this.questions });
            this.makeToast({ variant: 'success', msg: 'Quiz created successfully' });
            this.$router.push({ name: 'list-quizzes' });
          }
        } catch (error) {
          console.error('Error saving quiz:', error);
          this.makeToast({
            variant: 'danger',
            msg: error.response?.data?.message || 'Failed to save quiz. Please try again.',
          });
        } finally {
          this.isLoading = false;
        }
      }
    },

    async loadData() {
      if (this.$route.params.id) {
        try {
          const response = await this.getQuiz(this.$route.params.id);
          this.quiz = { ...response.data };
          this.questions = response.data.questions;
        } catch (error) {
          console.error('Error loading quiz:', error);
          this.makeToast({ variant: 'danger', msg: 'Failed to load quiz data' });
          this.$router.push({ name: 'list-quizzes' });
        }
      }
    },
  },

  async mounted() {
    await this.loadData();
  },
};
</script>

<style scoped>
.card {
  margin-bottom: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
  padding: 0.75rem 1.25rem;
  font-weight: 600;
}

.form-group {
  margin-bottom: 1.5rem;
}

.container {
  max-width: 600px;
  margin: 0 auto;
}

.title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 30px;
  text-align: left;
}

.btn:hover {
  opacity: 0.8;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.question-number-badge {
  display: flex;
  align-items: center;
  gap: 12px;
}

.question-number {
  background: #6c757d;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
}

.question-type-label {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.type-mcq {
  background: #d4edda;
  color: #155724;
}

.type-text {
  background: #e2d9f3;
  color: #6f42c1;
}

.type-tf {
  background: #ffeaa7;
  color: #d68910;
}

.delete-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 16px;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.delete-btn:hover {
  background: #f8d7da;
}

.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.form-input::placeholder {
  color: #adb5bd;
}

.points-input {
  width: 80px;
}

.options-section {
  margin-top: 20px;
}

.options-list {
  margin-bottom: 12px;
}

.option-item {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.option-actions {
  display: flex;
  margin-left: 8px;
}

.explanation-btn {
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 4px;
  margin-right: 4px;
  opacity: 0.7;
  transition: all 0.2s;
}

.explanation-btn:hover {
  opacity: 1;
  color: #007bff;
}

.remove-option-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  transition: all 0.2s;
}

.remove-option-btn:hover:not(:disabled) {
  opacity: 1;
}

.remove-option-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.option-radio {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.option-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.option-input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.remove-option-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-size: 14px;
  padding: 4px;
  border-radius: 4px;
  flex-shrink: 0;
}

.remove-option-btn:hover:not(:disabled) {
  background: #f8d7da;
}

.remove-option-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-option-btn {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 0;
}

.add-option-btn:hover {
  text-decoration: underline;
}

.radio-group {
  display: flex;
  gap: 24px;
  margin-top: 8px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #495057;
}

.radio-input {
  width: 16px;
  height: 16px;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
  font-size: 16px;
}

.quiz-questions-section {
  margin-bottom: 3rem;
}

.main-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.025em;
}

.dropdown-container {
  position: relative;
  display: inline-block;
}

.add-question-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 140px;
}

.add-question-btn:hover {
  background: #dbeafe;
  border-color: #bfdbfe;
}

.add-question-btn.active {
  background: #dbeafe;
  border-color: #3b82f6;
}

.plus-icon {
  width: 1rem;
  height: 1rem;
}

.chevron-icon {
  width: 1rem;
  height: 1rem;
  margin-left: auto;
  transition: transform 0.2s ease;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 50;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-10px);
  transition: all 0.2s ease;
  margin-top: 0.25rem;
}

.dropdown-menu.open {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.dropdown-item:hover {
  background: #f9fafb;
}

.question-type-badge {
  width: 2rem;
  height: 2rem;
  border-radius: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  flex-shrink: 0;
  /* opacity: 0.3; */
}

.question-type-badge.mc {
  --tw-bg-opacity: 1;
  background-color: rgb(220 252 231 / var(--tw-bg-opacity, 1));
  color: green;
}

.question-type-badge.tx {
  --tw-bg-opacity: 1;
  background-color: rgb(243 232 255 / var(--tw-bg-opacity, 1));
  color: purple;
}

.question-type-badge.tf {
  --tw-bg-opacity: 1;
  background-color: rgb(254 243 199 / var(--tw-bg-opacity, 1));
  color: orange;
}

.question-type-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.settings-container {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.settings-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
}

.setting-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
}

.setting-content {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  flex: 1;
}

.setting-icon {
  margin-top: 0.125rem;
}

.setting-info {
  flex: 1;
}

.setting-title {
  font-size: 1rem;
  font-weight: 500;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
}

.setting-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
}

.toggle-switch {
  position: relative;
  width: 48px;
  height: 24px;
  background-color: #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.toggle-switch.active {
  background-color: #3b82f6;
}

.toggle-slider {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-slider {
  transform: translateX(24px);
}

.toggle-checkmark {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.toggle-switch.active .toggle-checkmark {
  opacity: 1;
}

@media (max-width: 768px) {
  .settings-container {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style>
